'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/** Tinta verde profunda de firma — texto sobre el CTA salvia. */
const INK_ON_SAGE = '#0e1a13'

/** Divisor "enredadera": hairlines hacia el centro con una hoja salvia. */
function Vine() {
  return (
    <div className="flex items-center gap-3 w-full max-w-[240px]" aria-hidden>
      <span
        className="block h-px flex-1"
        style={{ background: 'linear-gradient(to right, transparent, color-mix(in srgb, var(--ms-accent) 55%, transparent))' }}
      />
      <span
        className="block w-[9px] h-[9px] rotate-45"
        style={{ background: 'var(--ms-accent)', borderRadius: '0 9px 0 9px' }}
      />
      <span
        className="block h-px flex-1"
        style={{ background: 'linear-gradient(to left, transparent, color-mix(in srgb, var(--ms-accent) 55%, transparent))' }}
      />
    </div>
  )
}

/**
 * Hero del tema "jardin" (botánico nocturno): verde bosque + crema + salvia.
 * Estructura full-bleed nocturna (media bajo overlay del token) con display
 * hairline (Italiana), divisores de enredadera con hoja, y la ciudad en chip
 * OVALADO — guiño a las etiquetas de lámina botánica. Familia cromática
 * deliberadamente distinta a Dark luxe. La barra superior usa modo sólido
 * (navSolid). Lee tokens del tema (--ms-*).
 */
export function HeroJardin({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)

  return (
    <section className="relative w-full overflow-hidden" style={{ background: 'var(--ms-bg)' }}>
      {/* Media a sangre (opcional) bajo el overlay del token */}
      {heroVideoUrl ? (
        <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" src={heroVideoUrl} />
      ) : heroImageUrl ? (
        <Image src={heroImageUrl} alt={businessName} fill priority sizes="100vw" className="object-cover" />
      ) : null}
      {hasMedia && <div className="absolute inset-0 z-10" style={{ background: 'var(--ms-hero-overlay)' }} />}

      {/* Atmósfera: glow salvia tenue arriba + viñeta de profundidad */}
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{ background: 'radial-gradient(120% 78% at 50% -12%, color-mix(in srgb, var(--ms-accent) 13%, transparent), transparent 56%)' }}
      />
      <div
        className="absolute inset-0 z-10 pointer-events-none"
        style={{ background: 'radial-gradient(78% 62% at 50% 46%, transparent 48%, rgba(4,10,7,0.6) 100%)' }}
      />

      {/* Contenido centrado */}
      <div
        className="relative z-20 mx-auto flex max-w-3xl flex-col items-center justify-center px-6 text-center"
        style={{ minHeight: 'min(680px, 88vh)' }}
      >
        <Vine />

        {city && (
          <span
            className="mt-6 inline-flex items-center px-5 py-1.5 rounded-full text-[10.5px] font-semibold uppercase tracking-[0.32em] pl-[calc(1.25rem+0.32em)]"
            style={{
              color: 'var(--ms-text-soft)',
              border: '1px solid color-mix(in srgb, var(--ms-accent) 45%, transparent)',
            }}
          >
            {city}
          </span>
        )}

        <h1
          className="mt-6 leading-[1.04] tracking-[0.01em] text-[clamp(44px,8.5vw,88px)]"
          style={{
            fontFamily: 'var(--ms-font-display)',
            color: 'var(--ms-text)',
            textShadow: hasMedia ? '0 4px 30px rgba(0,0,0,0.6)' : undefined,
          }}
        >
          {businessName}
        </h1>

        {tagline && (
          <p className="mt-4 max-w-xl text-[16px] sm:text-[18px] leading-relaxed" style={{ color: 'var(--ms-text-soft)' }}>
            {tagline}
          </p>
        )}

        <div className="mt-7">
          <Vine />
        </div>

        {/* Meta discreta */}
        <div
          className="mt-6 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-[12.5px] tracking-wide"
          style={{ color: 'var(--ms-text-muted)' }}
        >
          {statusLabel && (
            <span className="inline-flex items-center gap-2">
              <span
                className="w-[6px] h-[6px] rounded-full"
                style={{
                  background:
                    statusLabel.tone === 'open'
                      ? 'var(--ms-accent)'
                      : statusLabel.tone === 'soon'
                        ? '#f59e0b'
                        : 'var(--ms-text-muted)',
                }}
              />
              {statusLabel.text}
            </span>
          )}
          {statusLabel && <span aria-hidden>·</span>}
          <span>{t('pickupAvailable')}</span>
          {pickupWaitMinutes ? (
            <>
              <span aria-hidden>·</span>
              <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
            </>
          ) : null}
        </div>

        {/* CTAs: salvia sólido + outline salvia */}
        <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
          <button
            type="button"
            onClick={onViewMenu}
            className="inline-flex items-center px-9 py-4 text-[13px] font-bold uppercase tracking-[0.14em] transition hover:brightness-110 active:scale-[0.985]"
            style={{
              background: 'var(--ms-accent)',
              color: INK_ON_SAGE,
              borderRadius: 'var(--ms-radius)',
              boxShadow: '0 18px 42px -18px rgba(0,0,0,0.7)',
            }}
          >
            {t('orderOnline')}
          </button>
          {hasDelivery && onDelivery && (
            <button
              type="button"
              onClick={onDelivery}
              className="inline-flex items-center px-7 py-4 text-[12.5px] font-semibold uppercase tracking-[0.14em] transition hover:bg-white/5"
              style={{
                color: 'var(--ms-text)',
                borderRadius: 'var(--ms-radius)',
                border: '1px solid color-mix(in srgb, var(--ms-accent) 45%, transparent)',
              }}
            >
              {t('delivery')}
            </button>
          )}
        </div>
      </div>
    </section>
  )
}
